import { ptBR } from 'date-fns/locale'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'

import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '@/_components/ui'
import { cn } from '@/_lib/utils'

import { formatDate } from '../_helpers/date'

dayjs.extend(utc)
dayjs.extend(timezone)

type LoanDatePickerProps = {
  field: Pick<ControllerRenderProps, 'value' | 'onChange'>
  calendar?: Pick<React.ComponentProps<typeof Calendar>, 'fromDate' | 'toDate' | 'modifiers'>
  className?: string
  isDisabled?: boolean
}

export function LoanDatePicker({ field, isDisabled, className, calendar }: LoanDatePickerProps) {
  const timeZone = dayjs.tz.guess()

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    field.value ? dayjs(field.value).tz(timeZone).toDate() : undefined,
  )
  const [open, setOpen] = useState(false)

  const minimumSelectableDate = dayjs().tz(timeZone).add(3, 'day').startOf('day').toDate()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            `flex w-[260px] justify-start text-left font-normal hover:bg-accent ${!field.value && 'text-muted-foreground'}`,
            className,
          )}
          disabled={isDisabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {field.value ? formatDate(field.value) : <span>Selecione uma data</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(value) => {
            if (!value) return
            setSelectedDate(value)
            field.onChange(dayjs(value).format('YYYY-MM-DD'))
            setOpen(false)
          }}
          locale={ptBR}
          timeZone={timeZone}
          disabled={{ before: minimumSelectableDate }}
          {...calendar}
        />
      </PopoverContent>
    </Popover>
  )
}
